import { useEffect } from 'react';
import { EndpointLock, useLazyGetCaseStatusQuery } from '@/api';
import { BROADCAST_FORCE_CASE_STATUS_REFRESH, useFletchersBroadcast } from '@/broadcast';
import { useEndpointLock } from './useEndpointLock';

const CASE_STATUS_POLLING_INTERVAL_MS = 100 * 1000; // 100 seconds
// The backend can take a moment to flag a case once its clone starts, so the status is checked a few times over the
// next 20 seconds rather than once, then the 100 second polling carries on
const CASE_STATUS_REFRESH_DELAYS_MS = [1500, 5000, 10000, 20000];

interface ForceCaseStatusRefreshPayload {
  caseId: string;
}

/**
 * Locks the endpoints the backend refuses while `caseId` is being cloned (EndpointLock.CASE_CLONING), for as long as
 * the calling page is mounted. Reads case/status now, every 100 seconds after and whenever the tab regains focus,
 * which catches a clone started by anyone; a clone started from another tab in this browser is caught within seconds
 * through BROADCAST_FORCE_CASE_STATUS_REFRESH.
 */
export const useCaseCloningLock = (caseId: string | null | undefined) => {
  const [triggerGetCaseStatus, { currentData: caseStatus }] = useLazyGetCaseStatusQuery({
    pollingInterval: CASE_STATUS_POLLING_INTERVAL_MS,
    // Coming back to the tab checks straight away, so a clone that started or finished meanwhile shows at once
    refetchOnFocus: true,
  });

  useEndpointLock(EndpointLock.CASE_CLONING, !!caseId && !!caseStatus?.isCloning);

  useEffect(() => {
    if (!caseId) return;

    triggerGetCaseStatus({ caseId });

    let refreshTimeoutIds: ReturnType<typeof setTimeout>[] = [];

    const handleForceCaseStatusRefresh = (event: Event) => {
      const { payload } = (event as CustomEvent<{ payload?: ForceCaseStatusRefreshPayload }>).detail;

      if (payload?.caseId !== caseId) return;

      // A repeat signal starts the checks over rather than adding more; each check also restarts the 100 second countdown
      refreshTimeoutIds.forEach(clearTimeout);
      refreshTimeoutIds = CASE_STATUS_REFRESH_DELAYS_MS.map((delay) =>
        setTimeout(() => triggerGetCaseStatus({ caseId }), delay),
      );
    };

    window.addEventListener(BROADCAST_FORCE_CASE_STATUS_REFRESH, handleForceCaseStatusRefresh);

    return () => {
      window.removeEventListener(BROADCAST_FORCE_CASE_STATUS_REFRESH, handleForceCaseStatusRefresh);
      refreshTimeoutIds.forEach(clearTimeout);
    };
  }, [caseId, triggerGetCaseStatus]);
};

/** Has every tab on a case, this one included, re-read its status shortly - e.g. right after a clone of it starts. */
export const useForceCaseStatusRefresh = () => {
  const { sendMessageWithoutResponse } = useFletchersBroadcast<ForceCaseStatusRefreshPayload>();

  return (caseId: string) => {
    const payload: ForceCaseStatusRefreshPayload = { caseId };

    sendMessageWithoutResponse(BROADCAST_FORCE_CASE_STATUS_REFRESH, payload);
    // The channel only reaches other tabs, so tell this one directly
    window.dispatchEvent(new CustomEvent(BROADCAST_FORCE_CASE_STATUS_REFRESH, { detail: { payload } }));
  };
};
